import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { type ComponentType } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import { type CalloutLiveness, type CalloutProps, type CalloutTone } from './Callout.types';

/**
 * A short notice about the screen it sits on.
 *
 * Not a toast (which is about a write that just happened and goes away) and not
 * an `ErrorState` (which replaces the content it stands for). This one lives
 * *beside* working content and stays: "only the first fifteen sections are
 * shown", "matched is stricter than public", "you may read this but not change
 * it".
 *
 * It exists because the same shape had been spelled out by hand in eight places
 * across Settings, Admins and Questions, drifting to two border opacities and
 * two paddings — so a warning in one module and a warning in the next were
 * visibly different objects. The kit picks one of each; there is deliberately no
 * density knob, because that is the drift this replaces.
 */
export function Callout({
  id,
  tone = 'warning',
  title,
  children,
  liveness = 'status',
  action,
  className,
}: CalloutProps) {
  const Glyph = GLYPHS[tone];

  return (
    <div
      id={id}
      // `undefined` rather than `'none'`: a role attribute has no "off" value,
      // and an element carrying `role="none"` is removed from the tree entirely
      // — which would take the notice's text with it.
      role={LIVE_ROLES[liveness]}
      aria-live={liveness === 'status' ? 'polite' : undefined}
      className={cn(
        'flex items-start gap-sm rounded-md border p-md text-caption',
        TONES[tone],
        className,
      )}
    >
      <Glyph aria-hidden className={cn('mt-2xs size-4 shrink-0', GLYPH_TONES[tone])} />

      <div className="flex min-w-0 flex-1 flex-col gap-2xs">
        {title === undefined ? null : <p className="text-label text-foreground">{title}</p>}
        {/* A `div`, not a `p`: a caller may hand this a list or a paragraph and
            a `<p>` cannot legally contain either. */}
        <div className={title === undefined ? 'text-foreground' : 'text-muted-foreground'}>
          {children}
        </div>
      </div>

      {action === undefined ? null : <div className="shrink-0">{action}</div>}
    </div>
  );
}

/**
 * Whole class strings, never interpolated.
 *
 * Tailwind scans source text: `border-${tone}/60` is a string it has never seen,
 * so the rule is simply never emitted and the notice loses its border with no
 * error anywhere (the `scopeGridClass` lesson, P10.3a).
 */
const TONES: Record<CalloutTone, string> = {
  info: 'border-primary/60 bg-primary/10 text-foreground',
  warning: 'border-warning/60 bg-warning/10 text-foreground',
  danger: 'border-destructive/60 bg-destructive/10 text-foreground',
};

const GLYPH_TONES: Record<CalloutTone, string> = {
  info: 'text-primary',
  warning: 'text-warning',
  danger: 'text-destructive',
};

const GLYPHS: Record<
  CalloutTone,
  ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
> = {
  info: Info,
  warning: AlertTriangle,
  danger: AlertCircle,
};

const LIVE_ROLES: Record<CalloutLiveness, 'status' | 'alert' | undefined> = {
  status: 'status',
  alert: 'alert',
  off: undefined,
};
